// Design tokens — single source of truth for fonts, colors, spacing,
// radii, and z-indexes used across the app.
//
// MCM REBRAND (this file's role): the legacy values were tuned for a
// dark cream-on-near-black UI. They've been rewritten to map onto the
// MCM warm-cream palette anchored on the DAY theme of
// `src/experiments/mcm-cooking/themes.js`. Every consuming file
// (50+ across components / experiments / lib) keeps importing the
// same names and gets the new look automatically.
//
// Why static (not theme-aware): tokens.js exports are `const` — they
// can't call hooks. Files wrapped in `<MCMThemeProvider>` that want
// time-of-day blending (morning / day / dusk / night) should reach
// for `useTheme()` directly. Tokens give the consistent MCM static
// palette to the rest.
//
// What's NOT in this file: page-level inline `background: "#0f0f0f"`
// styles inside specific files (Kitchen, ItemCard, etc.). Those
// bypass tokens. They paint over the WarmBackdrop until each file is
// swept — see plan Phase 3.
//
// How to use these:
//
//   import { FONT, COLOR, RADIUS, SPACE, Z } from "../lib/tokens";
//   <div style={{
//     fontFamily: FONT.mono,
//     color: COLOR.gold,
//     borderRadius: RADIUS.md,
//     padding: SPACE.md,
//   }} />
//
// When to add a new token vs use a literal:
//   - If the value appears in 2+ places across different files, add
//     a token. Grep first; you'll usually find the pattern already
//     has a name.
//   - One-offs (a single accent color for a specific toast, a
//     specific animation delay) can stay literal — tokens exist to
//     deduplicate, not to enforce uniformity.
//   - Component-scoped constants belong in the component, not here.
//     This file is for values that cross component boundaries.

// Hex → rgba helper. Used to mix CHIP_TONES bg/border tints from the
// fg hex without scattering 8-character magic literals.
function withAlpha(colorStr, alpha) {
  if (!colorStr) return colorStr;
  if (colorStr.startsWith("#")) {
    const h = colorStr.slice(1);
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  const m = colorStr.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const parts = m[1].split(",").map((s) => s.trim());
    return `rgba(${parts[0]},${parts[1]},${parts[2]},${alpha})`;
  }
  return colorStr;
}

// ── FONTS ───────────────────────────────────────────────────────────
// Stacks include MCM-canonical first choice plus fallbacks so missing
// webfonts degrade gracefully. Authoritative type spec lives in the
// user's typography memory (memory/feedback_typography_reuse.md,
// approved 2026-04-27); this file mirrors the MCM token set so non-
// MCM components don't have to cross the experiments boundary.
//
//   * display   — Pale Martini (self-hosted). Hero / page-title
//                 shout — Home, Calendar, Courses headers. Use this
//                 for the "this is the title of the surface" voice,
//                 NOT for individual item titles.
//   * itemTitle — New Kansas italic 500 (display-context "this is
//                 your kitchen item / editorial title" voice). Use
//                 italic 500 for primary titles, italic 300 for
//                 contextual modifiers. THE editorial face for items.
//   * forma     — Forma DJR Deck. Functional UI labels, nav, chips,
//                 body, descriptions. Replaces DM Sans across the app.
//   * detail    — Instrument Serif italic. Soft / editorial BODY —
//                 kicker hints, secondary serif lines. NOT for titles
//                 (use itemTitle for those).
//   * mono      — DM Mono. RETIRED from the kicker register on
//                 2026-04-27 per typography memory; kept here ONLY
//                 for the legacy kickers we haven't migrated yet.
//                 Do NOT reach for this in new code — use FONT.forma
//                 with letterSpacing 0.18em uppercase instead.
//   * sans      — DM Sans. RETIRED in favor of forma; kept for
//                 legacy callers. Do NOT use in new code.
//   * serif     — Fraunces — RETIRED. Hard-deprecated; explicit
//                 user direction. The fallback chain points at
//                 instrument-serif so existing callers don't render
//                 Fraunces, but new code must NEVER read this token.
//                 Use itemTitle for editorial titles or detail for
//                 body serif. Removing the export would break legacy
//                 callers; the right deprecation is "don't add new
//                 references" plus comment-level signage like this.
// Forma DJR Deck — the workhorse UI face. Same string used by every
// "this should be Forma" token (forma, sans, mono — all aliased so
// the dual-namespace divergence between FONT and font (mcmTokens)
// can't bite us with stale font picks again).
const FORMA = "'forma-djr-deck', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";

export const FONT = {
  display:   "'Pale Martini', 'Truculenta', 'Futura', 'Trade Gothic', system-ui, sans-serif",
  itemTitle: "'new-kansas-tuned', 'new-kansas', 'Iowan Old Style', Georgia, serif",
  forma:     FORMA,
  // RETIRED 2026-05-02 — Instrument Serif and Fraunces are both
  // hard-banned per user directive. detail/serif/mono/sans all
  // alias to Forma DJR Deck. For editorial italic, use
  // FONT.forma with `fontStyle: "italic"` (Forma has italic faces);
  // for editorial display titles, use FONT.itemTitle (New Kansas).
  // Don't add new references to detail/serif — reach for forma or
  // itemTitle by intent.
  detail:    FORMA,
  serif:     FORMA,
  mono:      FORMA,
  sans:      FORMA,
};

// ── COLORS ──────────────────────────────────────────────────────────
// MCM warm-cream palette anchored on themes.day. Names are SEMANTIC —
// "gold" is the warm accent regardless of which hex is current.
//
// Inversion guide for old hardcoded styles:
//   - Page bg `#0f0f0f` / `#111` → COLOR.night (transparent — let the
//     root WarmBackdrop show through)
//   - Card bg `#141414`           → COLOR.ground (bright cream card)
//   - Body text `#f0ece4`         → COLOR.ink (dark on cream — the
//     light/dark axis flipped)
export const COLOR = {
  // Page / surface ladder — was a dark gray ladder; now a glass-on-
  // cream ladder. `night` is the fully-opaque page fill: any
  // fullscreen overlay (UserProfile, Settings, modals) uses this as
  // its background so it covers what's behind. The root WarmBackdrop
  // shows through because `pageShell` itself is transparent — only the
  // INNER overlay containers paint over it. `deep` and `ground` are
  // progressively more-opaque cream cards stacked above the backdrop.
  night:  "#FAF6EE",                     // opaque cream page fill (= day theme cream)
  deep:   "rgba(255,255,255,0.42)",      // glassFillLite — frosted underlay
  ground: "rgba(255,255,255,0.75)",      // glassFillHeavy — bright card
  soil:   "rgba(255,255,255,0.62)",      // glassFill — neutral surface
  edge:   "rgba(30,30,30,0.08)",         // hairline — subtle divider
  border: "rgba(30,30,30,0.14)",         // standard border (slightly stronger hairline)
  sub:    "#8A8072",                     // inkFaint — low-contrast text
  muted:  "#5A5A5A",                     // inkMuted — mid-contrast text
  dim:    "#5A5A5A",                     // inkMuted — three-step dark ladder collapses to two on cream
  ink:    "#1E1E1E",                     // ink — body text (dark on cream)

  // Semantic accents — mapped to MCM-canonical palette.
  // Mustard family (was gold/amber).
  gold:      "#D4A637",
  goldDim:   "rgba(212,166,55,0.32)",
  goldDeep:  "rgba(212,166,55,0.18)",
  // Aqua family (was sky blue).
  sky:       "#6FAF9B",
  skyDeep:   "rgba(111,175,155,0.18)",
  skyBorder: "rgba(111,175,155,0.32)",
  // Teal family (was leaf green — fresh / cook-ready).
  leaf:      "#2F8F83",
  leafMuted: "#2F8F83",
  leafDim:   "rgba(47,143,131,0.32)",
  leafDeep:  "rgba(47,143,131,0.14)",
  // Burnt family (was rose / destructive — warning red repurposes to
  // MCM's burnt orange; reads as "alert" without harsh chroma on cream).
  rose:      "#D96B2B",
  roseDim:   "rgba(217,107,43,0.32)",
  roseDeep:  "rgba(217,107,43,0.14)",
  // Amber → mustard (semantic doublet).
  amber:     "#D4A637",
  // Plum → warmBrown (no purple in MCM; warmBrown carries the same
  // "subtle accent" semantic).
  plum:      "#7A4E2D",
};

// ── SPACE ───────────────────────────────────────────────────────────
// Numeric scale unchanged — values are structural, not visual; the MCM
// screens use compatible numbers.
export const SPACE = {
  xs:  4,
  sm:  6,
  md:  8,
  lg:  10,
  xl:  12,
  xxl: 14,
  section: 18,
  hero: 22,
};

// ── RADIUS ──────────────────────────────────────────────────────────
// Corner radii. Cards `lg`, chips `sm`, sheet top corners `sheet`.
export const RADIUS = {
  xs: 2,
  sm: 4,
  md: 8,
  lg: 10,
  xl: 12,
  xxl: 16,
  sheet: 20,
  pill: 9999,
};

// ── Z-INDEX LAYERS ──────────────────────────────────────────────────
// Higher number = on top. Leave gaps (10s) so one-off overrides can
// slot between layers without renumbering.
export const Z = {
  nav:      100,  // bottom tab bar
  backdrop: 150,  // modal backdrop base
  modal:    160,  // AddItemModal, scan confirm
  sheet:    180,  // IngredientDetailSheet, scan picker sheets
  card:     320,  // ItemCard (primary drill)
  picker:   340,  // LinkIngredient (over cards)
  confirm:  350,  // delete confirmation (over everything user-destructive)
  toast:    400,  // toast notifications
};

// ── TRANSITIONS ─────────────────────────────────────────────────────
export const MOTION = {
  fast:   "0.12s ease",
  normal: "0.18s ease",
  slow:   "0.30s ease",
};

// Framer-motion friendly spring/tween presets.
export const SPRING = {
  tap:    { type: "spring", stiffness: 520, damping: 32, mass: 0.6 },
  sheet:  { type: "spring", stiffness: 360, damping: 34, mass: 0.9 },
  stagger:{ type: "spring", stiffness: 300, damping: 28, mass: 0.8 },
  soft:   { type: "spring", stiffness: 220, damping: 26, mass: 1 },
};

// ── CHIP TONES (reserved color axis palette) ────────────────────────
// CLAUDE.md reserved-color hierarchy. The fg hexes are LOAD-BEARING
// identity markers (CLAUDE.md "Reserved color hierarchy"). DO NOT
// change them. bg / border were dark-on-near-black tints; rewritten to
// faint axis-tinted alpha on the cream backdrop.
const AXIS_FG = {
  canonical:    "#b8a878", // tan
  cut:          "#a8553a", // rust
  category:     "#e07a3a", // orange
  location:     "#7eb8d4", // blue (STORED IN tile)
  state:        "#c7a8d4", // purple
  ingredients:  "#f5c842", // yellow
};
export const CHIP_TONES = {
  canonical: {
    fg: AXIS_FG.canonical,
    bg: withAlpha(AXIS_FG.canonical, 0.10),
    border: withAlpha(AXIS_FG.canonical, 0.32),
  },
  cut: {
    fg: AXIS_FG.cut,
    bg: withAlpha(AXIS_FG.cut, 0.10),
    border: withAlpha(AXIS_FG.cut, 0.32),
  },
  category: {
    fg: AXIS_FG.category,
    bg: withAlpha(AXIS_FG.category, 0.10),
    border: withAlpha(AXIS_FG.category, 0.32),
  },
  location: {
    fg: AXIS_FG.location,
    bg: withAlpha(AXIS_FG.location, 0.10),
    border: withAlpha(AXIS_FG.location, 0.32),
  },
  // Muted LOCATION variant (the fridge/pantry/freezer container axis,
  // one tier above STORED IN tile). Same blue, even fainter tint.
  locationMuted: {
    fg: withAlpha(AXIS_FG.location, 0.7),
    bg: withAlpha(AXIS_FG.location, 0.06),
    border: withAlpha(AXIS_FG.location, 0.20),
  },
  state: {
    fg: AXIS_FG.state,
    bg: withAlpha(AXIS_FG.state, 0.10),
    border: withAlpha(AXIS_FG.state, 0.32),
  },
  ingredients: {
    fg: AXIS_FG.ingredients,
    bg: withAlpha(AXIS_FG.ingredients, 0.10),
    border: withAlpha(AXIS_FG.ingredients, 0.32),
  },
};

// ── CHIP STYLES ─────────────────────────────────────────────────────
// Reusable chip styles. DM Mono 9px, letter-spaced, emoji + UPPERCASE
// when set; dashed hairline "+ set <axis>" when unset. Keyed by
// CHIP_TONES tone.
//
// USAGE:
//   import { SET_CHIP, UNSET_CHIP, CHIP_TONES } from "../lib/tokens";
//   <button style={SET_CHIP(CHIP_TONES.category)}>🍞 BREAD</button>
//   <button style={UNSET_CHIP}>+ set category</button>
export const SET_CHIP = (tone) => ({
  display: "inline-flex", alignItems: "center", gap: 4,
  fontFamily: FONT.mono, fontSize: 9,
  color: tone.fg, background: tone.bg,
  border: `1px solid ${tone.border}`,
  borderRadius: RADIUS.sm, padding: "3px 7px",
  minHeight: 22,
  letterSpacing: "0.08em", cursor: "pointer",
  transition: `background ${MOTION.fast}, border-color ${MOTION.fast}, transform ${MOTION.fast}`,
});
// UNSET_CHIP — dashed affordance for unset identity axes. Hairline
// dashed border on cream so the dim color stays legible (was
// dim-on-dark; now dim-on-cream is fine without bumping contrast).
export const UNSET_CHIP = {
  display: "inline-flex", alignItems: "center",
  fontFamily: FONT.mono, fontSize: 9,
  color: COLOR.dim, background: "transparent",
  border: `1px dashed ${COLOR.border}`,
  borderRadius: RADIUS.sm, padding: "2px 7px",
  minHeight: 22,
  letterSpacing: "0.08em", cursor: "pointer",
  transition: `border-color ${MOTION.fast}, color ${MOTION.fast}, background ${MOTION.fast}`,
};

// ── PICKER STYLES (ModalSheet picker content) ───────────────────────
// Every ModalSheet that functions as an axis picker (CANONICAL,
// CATEGORY, STORED IN, etc.) renders a kicker label in DM Mono tinted
// the axis color, an italic editorial title, and a list of option
// buttons. These styles keep all of them visually synchronized.
export const pickerKicker = (color) => ({
  fontFamily: FONT.mono, fontSize: 10,
  color, letterSpacing: "0.18em",
  marginBottom: 10,
  textTransform: "uppercase",
});
// Editorial-weight headline for ModalSheet pickers. Instrument Serif
// italic carries the weight; the kicker above supplies the category
// tag; the description paragraph below supplies the instructions.
// This shape (tag → hero line → body) is the sheet's brand signature.
export const pickerTitle = {
  fontFamily: FONT.serif, fontSize: 26,
  fontStyle: "italic", color: COLOR.ink,
  fontWeight: 500, margin: "0 0 10px", lineHeight: 1.15,
  letterSpacing: "-0.01em",
};
// Muted body copy that sits under pickerTitle to explain the
// picker's purpose.
export const pickerBody = {
  fontFamily: FONT.sans, fontSize: 13,
  color: COLOR.dim, lineHeight: 1.55,
  margin: "0 0 16px",
};
export function pickerOptionStyle(active, tone) {
  return {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 14px",
    background: active ? tone.bg : COLOR.ground,
    border: `1px solid ${active ? tone.border : COLOR.edge}`,
    borderRadius: RADIUS.lg,
    textAlign: "left", cursor: "pointer",
    color: COLOR.ink,
    fontFamily: FONT.sans,
    transition: `background ${MOTION.fast}, border-color ${MOTION.fast}, transform ${MOTION.fast}`,
  };
}
